import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Zap, QrCode, CreditCard, Sparkles, Lock, Instagram, Facebook, Twitter, Music2, Headphones, Check } from "lucide-react";
import { Logo } from "../components/Logo";
import PublicChat from "../components/PublicChat";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const SOCIAL_ICONS = { tiktok: Music2, instagram: Instagram, x: Twitter, facebook: Facebook };
const SOCIAL_LABELS = { tiktok: "TikTok", instagram: "Instagram", x: "X", facebook: "Facebook" };

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/1579863e-1f94-40de-968b-7b3e75bdbd48/images/8306c448139d2647f242ccb7e3a5fcf8de2cf1c0b19ac3343265fa99abee9650.jpeg";

const FEATURES = [
  { icon: Zap, title: "Instant sends", body: "Move money to any Cash Tag in seconds on a real, auditable ledger." },
  { icon: QrCode, title: "QR receive", body: "Share your Cash Tag or QR code and get paid right away." },
  { icon: CreditCard, title: "Virtual cards", body: "Create a card, freeze it in one tap, and stay in control." },
  { icon: Sparkles, title: "AI assistant", body: "Ask about your spending, fees and activity — anytime." },
];

export default function Landing() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [social, setSocial] = useState({});
  useEffect(() => { if (!loading && user) nav("/app", { replace: true }); }, [user, loading, nav]);
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/social");
      setSocial(data);
      const links = Object.values(data).filter(Boolean);
      if (links.length) {
        let el = document.getElementById("jc-social-jsonld");
        if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "jc-social-jsonld"; document.head.appendChild(el); }
        el.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "Organization", name: "JackCash", legalName: "JackCash LLC", url: "https://www.jackcashpay.com", sameAs: links });
      }
    } catch (e) { /* public, non-blocking */ }
  })(); }, []);

  return (
    <div className="min-h-screen w-full bg-background bg-mesh overflow-x-hidden">
      {/* Navbar */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo size={34} />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#security" className="hover:text-foreground transition-colors">Security</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-login-link" className="px-4 py-2 text-sm font-semibold text-primary hover:bg-secondary rounded-full transition-colors">Sign in</Link>
            <button data-testid="landing-signup-btn" onClick={() => nav("/signup")}
              className="px-4 py-2 text-sm font-semibold rounded-full bg-primary text-primary-foreground shadow-[0_8px_22px_rgba(75,35,204,0.3)] transition-transform duration-200 active:scale-95">
              Get started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-16 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-bold text-primary bg-primary/10 px-3 py-1.5 rounded-full">
            <Sparkles size={13} /> Premium digital wallet
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.05] mt-6">
            Send money in <span className="text-primary">seconds</span>.<br className="hidden sm:block" /> Not days.
          </h1>
          <p className="text-muted-foreground mt-5 text-base sm:text-lg leading-relaxed max-w-md">
            Jack Cash is the simplest way to send, receive and manage your money with a personal Cash Tag — built on a real, secure ledger.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <button data-testid="landing-get-started-btn" onClick={() => nav("/signup")}
              className="h-14 px-7 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(75,35,204,0.35)] transition-transform duration-200 active:scale-[0.98]">
              Create your account <ArrowRight size={18} />
            </button>
            <button data-testid="landing-signin-btn" onClick={() => nav("/login")}
              className="h-14 px-7 rounded-2xl bg-card border border-border font-semibold transition-transform duration-200 active:scale-[0.98]">
              I already have an account
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Lock size={15} className="text-primary" /> Bank-grade security</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={15} className="text-primary" /> Real auditable ledger</span>
          </div>
        </div>

        {/* Phone mockup */}
        <div className="relative flex justify-center lg:justify-end animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute inset-0 -z-10 blur-3xl opacity-60"
            style={{ background: "radial-gradient(circle at 60% 40%, rgba(107,59,245,0.35), transparent 60%)" }} />
          <img src={HERO_IMG} alt="Jack Cash app" data-testid="hero-image"
            className="w-[280px] sm:w-[340px] lg:w-[400px] drop-shadow-[0_30px_60px_rgba(75,35,204,0.35)] rounded-[2rem]" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-center">Everything your money needs</h2>
        <p className="text-muted-foreground text-center mt-2 max-w-lg mx-auto">A complete wallet — send, receive, cards, crypto and an AI assistant, all in one clean app.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-3xl bg-card border border-border p-6 hover:shadow-[0_18px_40px_rgba(75,35,204,0.12)] transition-shadow">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-primary mb-4"><Icon size={22} /></span>
              <h3 className="font-display text-lg font-semibold">{title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Jack Cash — fee-friendly band */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-center">Keep more of your money</h2>
        <p className="text-muted-foreground text-center mt-2 max-w-lg mx-auto">Simple, transparent and built around you — no surprises.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {[
            "No monthly fees",
            "No minimum balance",
            "Fees always shown upfront",
            "Your money, in your control",
          ].map((t) => (
            <div key={t} className="flex items-center gap-3 rounded-2xl bg-card border border-border p-5">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-success/10 text-success shrink-0"><Check size={18} /></span>
              <span className="font-semibold text-sm">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 24/7 human support */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-[2rem] bg-card border border-border p-8 sm:p-10 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-5"><Headphones size={26} /></span>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">24/7 support with a real human</h2>
            <p className="text-muted-foreground mt-3 max-w-md leading-relaxed">
              Chat in-app or ask for a call whenever you need help and talk to a real person — 24 hours a day, 7 days a week. No endless menus, no bots-only.
            </p>
            <Link to="/legal/support" data-testid="landing-support-link"
              className="inline-flex items-center gap-2 mt-6 h-12 px-6 rounded-2xl bg-secondary font-semibold text-sm transition-transform active:scale-[0.98]">
              See support options <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "24/7", v: "Live human support" },
              { k: "Instant", v: "Cash Tag transfers" },
              { k: "AI", v: "Fraud monitoring" },
              { k: "USD/HTG", v: "Built-in converter" },
            ].map((s) => (
              <div key={s.v} className="rounded-2xl bg-secondary/50 p-5 text-center">
                <div className="font-display text-2xl font-bold text-primary">{s.k}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get the app */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-[2rem] bg-secondary/40 border border-border p-8 sm:p-10 text-center">
          <img src="/favicon.png" alt="Jack Cash app" className="w-16 h-16 rounded-2xl mx-auto mb-4 shadow-lg" data-testid="getapp-app-icon" />
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Get Jack Cash on your phone</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">Our mobile apps are on the way. Download them on iOS and Android — coming soon.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
            <a data-testid="appstore-badge" href={social.appstore || undefined} target={social.appstore ? "_blank" : undefined} rel="noopener noreferrer"
              className={`relative inline-flex items-center gap-3 h-14 px-5 rounded-2xl bg-foreground text-background select-none ${social.appstore ? "hover:opacity-90 transition-opacity" : "opacity-90 cursor-default pointer-events-none"}`}>
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M16.365 1.43c0 1.14-.42 2.2-1.12 2.98-.76.86-2 1.52-3.02 1.44-.13-1.1.44-2.26 1.1-2.98.74-.82 2.05-1.42 3.04-1.44zM20.7 17.2c-.56 1.29-.83 1.86-1.55 3-.99 1.58-2.39 3.55-4.12 3.56-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.8-4.04-3.38C.14 17.35-.46 12.9 1.06 10.05 2.14 8 4 6.76 5.78 6.76c1.78 0 2.9 1 4.37 1 1.43 0 2.3-1 4.36-1 1.56 0 3.21.85 4.39 2.32-3.86 2.12-3.23 7.63.8 9.12z"/></svg>
              <span className="text-left leading-tight"><span className="block text-[10px] opacity-70">Download on the</span><span className="block text-base font-semibold">App Store</span></span>
              {!social.appstore && <span className="absolute -top-2 -right-2 text-[9px] font-bold bg-primary text-white px-2 py-0.5 rounded-full">SOON</span>}
            </a>
            <a data-testid="googleplay-badge" href={social.playstore || undefined} target={social.playstore ? "_blank" : undefined} rel="noopener noreferrer"
              className={`relative inline-flex items-center gap-3 h-14 px-5 rounded-2xl bg-foreground text-background select-none ${social.playstore ? "hover:opacity-90 transition-opacity" : "opacity-90 cursor-default pointer-events-none"}`}>
              <svg viewBox="0 0 24 24" width="22" height="22"><path fill="#00D2FF" d="M3.6 1.8 13.3 12 3.6 22.2c-.4-.3-.6-.8-.6-1.4V3.2c0-.6.2-1.1.6-1.4z"/><path fill="#00F076" d="M3.6 1.8c.3-.2.7-.2 1.1 0l12 6.9-2.9 3L3.6 1.8z"/><path fill="#FFCE00" d="M19.6 11.1c.8.5.8 1.8 0 2.3l-2.8 1.6-3-3 3-3 2.8 2.1z"/><path fill="#FF3A44" d="M4.7 22.2c-.4.2-.8.2-1.1 0l10.2-9.9 2.9 3-12 6.9z"/></svg>
              <span className="text-left leading-tight"><span className="block text-[10px] opacity-70">GET IT ON</span><span className="block text-base font-semibold">Google Play</span></span>
              {!social.playstore && <span className="absolute -top-2 -right-2 text-[9px] font-bold bg-primary text-white px-2 py-0.5 rounded-full">SOON</span>}
            </a>
          </div>
          <p className="text-xs text-muted-foreground mt-5">In the meantime, use Jack Cash right in your browser — <button onClick={() => nav("/signup")} className="text-primary font-semibold underline-offset-2 hover:underline">create your account</button>.</p>
        </div>
      </section>

      {/* Security / CTA band */}
      <section id="security" className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="relative overflow-hidden rounded-[2rem] p-8 sm:p-12 text-white text-center"
          style={{ background: "linear-gradient(135deg,#6B3BF5 0%,#4B23CC 55%,#3A1A9F 100%)" }}>
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/10" />
          <div className="absolute -left-8 bottom-0 w-40 h-40 rounded-full bg-white/5" />
          <div className="relative">
            <ShieldCheck size={36} className="mx-auto mb-4 opacity-90" />
            <h2 className="font-display text-2xl sm:text-4xl font-light tracking-tight">Serious security, by design</h2>
            <p className="text-white/80 mt-3 max-w-xl mx-auto">Protected sessions, server-side authorization, idempotent transactions and full audit logs on every money movement.</p>
            <button data-testid="cta-get-started-btn" onClick={() => nav("/signup")}
              className="mt-7 h-14 px-8 rounded-2xl bg-white text-primary font-semibold inline-flex items-center gap-2 transition-transform duration-200 active:scale-[0.98]">
              Open your free account <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border">
        <Logo size={28} />
        <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-medium text-muted-foreground">
          <Link to="/legal/about" data-testid="footer-about" className="hover:text-foreground">About</Link>
          <Link to="/legal/terms" data-testid="footer-terms" className="hover:text-foreground">Terms</Link>
          <Link to="/legal/privacy" data-testid="footer-privacy" className="hover:text-foreground">Privacy</Link>
          <Link to="/legal/faq" data-testid="footer-faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/legal/support" data-testid="footer-support" className="hover:text-foreground">Support</Link>
        </nav>
        <div className="flex items-center gap-3">
          {Object.entries(SOCIAL_ICONS).map(([key, Icon]) => social[key] ? (
            <a key={key} href={social[key]} target="_blank" rel="noopener noreferrer"
              data-testid={`footer-social-${key}`} aria-label={SOCIAL_LABELS[key]}
              className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
              <Icon size={16} />
            </a>
          ) : null)}
        </div>
        <p className="text-xs text-muted-foreground">© {[2025, new Date().getFullYear()].filter((v, i, a) => a.indexOf(v) === i).join("–")} Jack Cash LLC</p>
      </footer>

      <PublicChat />
    </div>
  );
}
